import { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  Copy,
  ExternalLink,
  RefreshCw,
  Sparkles,
  Star,
  Trash2,
} from "lucide-react";
import { domainService, type OwnerDomain } from "../../domains";
import {
  dnsRecordName,
  isApexHostname,
  normalizeDomainInput,
} from "../../../lib/hostname";
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  Hint,
  IconButton,
  Input,
  Notice,
} from "../../../components/ui";

type DomainsSectionProps = {
  siteId: string;
  isSubscribed: boolean;
  isPublished: boolean;
  siteSubdomain: string | null;
  subscribing: boolean;
  onSubscribe: () => void;
};

// DNS propagation takes minutes, so the Cloudflare verify poll backs off
// 10s → 30s → 60s rather than polling flat-out — keeps API usage
// proportional to the wait, not to the tab being open.
const POLL_STEPS_MS = [10_000, 30_000, 60_000];

const STATUS_LABEL: Record<OwnerDomain["status"], string> = {
  pending: "Waiting for DNS",
  verifying: "Verifying",
  active: "Live",
  failed: "Needs attention",
};

const STATUS_TONE: Record<OwnerDomain["status"], BadgeTone> = {
  pending: "warning",
  verifying: "warning",
  active: "success",
  failed: "danger",
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

type RecordRowProps = {
  label: string;
  value: string;
  hint?: string;
  copied: boolean;
  onCopy: (value: string) => void;
};

const RecordRow = ({ label, value, hint, copied, onCopy }: RecordRowProps) => (
  <div className="flex items-center justify-between gap-3 border-t border-hazumu-border py-2 first:border-t-0">
    <div className="min-w-0">
      <p className="text-xs font-medium text-hazumu-muted">{label}</p>
      <p className="truncate font-mono text-[13px] text-hazumu-ink">{value}</p>
      {hint && <p className="text-[11px] text-hazumu-muted">{hint}</p>}
    </div>
    <IconButton
      aria-label={`Copy ${label}`}
      className="border border-hazumu-border bg-hazumu-white"
      onClick={() => onCopy(value)}
      size="sm"
      title={copied ? "Copied" : `Copy ${value}`}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </IconButton>
  </div>
);

const ApexGuidance = ({ hostname }: { hostname: string }) => (
  <Notice size="sm" tone="warning">
    <p className="font-semibold">
      {hostname} is a bare domain — it needs one extra step
    </p>
    <p className="mt-1.5 text-hazumu-muted">
      Most DNS providers don’t allow a CNAME on a bare domain. Pick whichever is
      easiest for you:
    </p>
    <ol className="mt-1.5 list-decimal space-y-1.5 pl-4 text-hazumu-muted">
      <li>
        <strong className="text-hazumu-ink">
          Forward {hostname} to www.{hostname}
        </strong>{" "}
        at your registrar — free at GoDaddy, Namecheap and most others — then
        add <strong className="text-hazumu-ink">www.{hostname}</strong> here as
        a second domain.
      </li>
      <li>
        <strong className="text-hazumu-ink">Use CNAME flattening</strong> if
        your DNS provider supports it (also called ALIAS or ANAME — Cloudflare,
        Route53, DNSimple and Porkbun do). The CNAME above works at the apex
        there.
      </li>
      <li>
        <strong className="text-hazumu-ink">
          Move your DNS to Cloudflare’s free plan
        </strong>{" "}
        — it handles bare domains automatically, but means moving all your DNS
        records, so try the first options first.
      </li>
    </ol>
    <p className="mt-1.5 text-hazumu-muted">
      Delete any leftover A records on {hostname} first — records left over from
      a previous host override forwarding and block verification.
    </p>
  </Notice>
);

type DomainCardProps = {
  domain: OwnerDomain;
  isPublished: boolean;
  isSubscribed: boolean;
  busy: boolean;
  actionError: string;
  copied: string;
  onCopy: (value: string) => void;
  onCheck: () => void;
  onSetPrimary: () => void;
  onRemove: () => void;
};

const DomainCard = ({
  domain,
  isPublished,
  isSubscribed,
  busy,
  actionError,
  copied,
  onCopy,
  onCheck,
  onSetPrimary,
  onRemove,
}: DomainCardProps) => {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const apex = isApexHostname(domain.hostname);
  const wwwPair = domain.hostname.startsWith("www.")
    ? domain.hostname.slice(4)
    : null;
  const settled = domain.status === "active";
  // An active domain stops serving the moment the subscription grace period
  // ends (the public read path stops exposing it) — the row still exists and
  // comes back automatically on resubscribe, so show it as paused, not live.
  const paused = settled && !isSubscribed;

  return (
    <Card>
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-semibold text-hazumu-ink">
          {domain.hostname}
        </p>
        <div className="flex shrink-0 items-center gap-1.5">
          {domain.is_primary && <Badge tone="strong">Primary</Badge>}
          <Badge tone={paused ? "warning" : STATUS_TONE[domain.status]}>
            {paused ? "Paused" : STATUS_LABEL[domain.status]}
          </Badge>
        </div>
      </div>

      {settled ? (
        <div className="mt-2 space-y-1.5">
          <a
            className="inline-flex items-center gap-1 text-xs font-semibold text-hazumu-terracotta transition hover:text-hazumu-ink"
            href={`https://${domain.hostname}`}
            rel="noreferrer"
            target="_blank"
          >
            {domain.hostname}
            <ExternalLink size={12} />
          </a>
          {paused && (
            <Hint>
              Paused because your subscription has ended — {domain.hostname}{" "}
              isn’t serving right now. Resubscribe and it comes back on its own;
              nothing to set up again.
            </Hint>
          )}
          {!isPublished && (
            <Hint>
              Verified — publish your site and {domain.hostname} goes live.
            </Hint>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {domain.expected_cname ? (
            <div className="rounded-xl border border-hazumu-border bg-hazumu-white px-3">
              <RecordRow
                copied={copied === "CNAME"}
                label="Type"
                onCopy={onCopy}
                value="CNAME"
              />
              <RecordRow
                copied={copied === domain.hostname}
                hint={`Or just “${dnsRecordName(domain.hostname)}” if your provider asks for a host`}
                label="Name"
                onCopy={onCopy}
                value={domain.hostname}
              />
              <RecordRow
                copied={copied === domain.expected_cname}
                label="Points to"
                onCopy={onCopy}
                value={domain.expected_cname}
              />
            </div>
          ) : (
            <Notice size="sm" tone="info">
              This domain was added before DNS instructions were recorded —
              remove it and add it again to get your record.
            </Notice>
          )}

          {apex ? (
            <ApexGuidance hostname={domain.hostname} />
          ) : (
            <Hint>
              Add this CNAME record wherever your DNS is managed.
              {wwwPair
                ? ` Also add ${wwwPair} — the same page will guide you through forwarding it to ${domain.hostname}.`
                : ""}
            </Hint>
          )}

          {domain.status === "pending" && !domain.edge_registered && (
            <Notice size="sm">
              This domain never reached the verification service — remove it and
              add it again.
            </Notice>
          )}
          {domain.status === "failed" && (
            <Notice size="sm">
              {domain.failure_reason ??
                "Verification failed — check the record above and try again."}
            </Notice>
          )}
          {domain.status !== "failed" && (
            <Hint>
              We check automatically — DNS changes can take up to 48 hours to
              reach us.
            </Hint>
          )}
        </div>
      )}

      {actionError && (
        <Notice className="mt-3" size="sm">
          {actionError}
        </Notice>
      )}

      {confirmingRemove ? (
        <Notice className="mt-3" size="sm">
          <p className="font-semibold">
            Remove {domain.hostname}? Visitors will no longer reach your site
            there.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              disabled={busy}
              onClick={onRemove}
              size="sm"
              variant="danger"
            >
              <Trash2 size={14} />
              Remove domain
            </Button>
            <Button
              disabled={busy}
              onClick={() => setConfirmingRemove(false)}
              size="sm"
              variant="ghost"
            >
              Keep
            </Button>
          </div>
        </Notice>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {!settled && (
            <Button
              disabled={busy}
              onClick={onCheck}
              size="sm"
              variant="secondary"
            >
              <RefreshCw size={14} />
              {domain.status === "failed" ? "Try again" : "Check now"}
            </Button>
          )}
          {settled && !domain.is_primary && (
            <Button
              disabled={busy}
              onClick={onSetPrimary}
              size="sm"
              variant="secondary"
            >
              <Star size={14} />
              Make primary
            </Button>
          )}
          <Button
            destructive
            disabled={busy}
            onClick={() => setConfirmingRemove(true)}
            size="sm"
            variant="secondary"
          >
            <Trash2 size={14} />
            Remove
          </Button>
        </div>
      )}
    </Card>
  );
};

export const DomainsSection = ({
  siteId,
  isSubscribed,
  isPublished,
  siteSubdomain,
  subscribing,
  onSubscribe,
}: DomainsSectionProps) => {
  const [domains, setDomains] = useState<OwnerDomain[] | null>(null);
  const [input, setInput] = useState("");
  // The guidance under the input reacts to the last blurred value, not every
  // keystroke — apex detection mid-typing (e.g. "example.c") flickers.
  const [hintHost, setHintHost] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState("");
  const polling = useRef(false);

  const updateDomain = useCallback((updated: OwnerDomain) => {
    setDomains(
      (current) =>
        current?.map((item) => (item.id === updated.id ? updated : item)) ??
        current,
    );
  }, []);

  const checkDomain = useCallback(
    async (domainId: string) => {
      try {
        const { domain } = await domainService.verify(domainId);
        updateDomain(domain);
        setActionErrors((current) => ({ ...current, [domainId]: "" }));
        return "";
      } catch (checkError) {
        return errorMessage(checkError, "Could not check the domain.");
      }
    },
    [updateDomain],
  );

  useEffect(() => {
    let active = true;
    domainService
      .list(siteId)
      .then((loaded) => {
        if (active) setDomains(loaded);
      })
      .catch((loadError: unknown) => {
        if (active)
          setError(
            errorMessage(loadError, "Could not load your custom domains."),
          );
      });
    return () => {
      active = false;
    };
  }, [siteId]);

  // Poll Cloudflare via manage-domain while anything is mid-verification, so
  // the status updates without a manual refresh. Rows that never reached the
  // edge (edge_registered false) have nothing to poll.
  const pendingKey = (domains ?? [])
    .filter(
      (domain) =>
        (domain.status === "pending" || domain.status === "verifying") &&
        domain.edge_registered,
    )
    .map((domain) => domain.id)
    .join(",");

  useEffect(() => {
    if (!pendingKey) return;
    const ids = pendingKey.split(",");
    const tick = async () => {
      if (polling.current) return;
      polling.current = true;
      try {
        // Polling failures are transient — surfaced by the manual buttons.
        for (const id of ids) await checkDomain(id);
      } finally {
        polling.current = false;
      }
    };
    void tick();
    let step = 0;
    let timer = 0;
    const schedule = () => {
      timer = window.setTimeout(() => {
        step = Math.min(step + 1, POLL_STEPS_MS.length - 1);
        void tick();
        schedule();
      }, POLL_STEPS_MS[step]);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, [pendingKey, checkDomain]);

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      return;
    }
    setCopied(value);
    window.setTimeout(
      () => setCopied((current) => (current === value ? "" : current)),
      1500,
    );
  };

  const add = async () => {
    if (!input.trim()) return;
    setAdding(true);
    setError("");
    try {
      const result = await domainService.add(siteId, input);
      setDomains((current) => [
        ...(current ?? []).filter((item) => item.id !== result.domain.id),
        result.domain,
      ]);
      setInput("");
      setHintHost("");
    } catch (addError) {
      setError(errorMessage(addError, "Could not add the domain."));
    } finally {
      setAdding(false);
    }
  };

  const check = async (domainId: string) => {
    setBusyId(domainId);
    const message = await checkDomain(domainId);
    if (message)
      setActionErrors((current) => ({ ...current, [domainId]: message }));
    setBusyId(null);
  };

  const makePrimary = async (domainId: string) => {
    setBusyId(domainId);
    try {
      const updated = await domainService.setPrimary(domainId);
      setDomains((current) =>
        (current ?? []).map((item) => ({
          ...item,
          is_primary: item.id === updated.id,
        })),
      );
    } catch (primaryError) {
      setActionErrors((current) => ({
        ...current,
        [domainId]: errorMessage(
          primaryError,
          "Could not set the primary domain.",
        ),
      }));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (domainId: string) => {
    setBusyId(domainId);
    try {
      await domainService.remove(domainId);
      setDomains((current) =>
        (current ?? []).filter((item) => item.id !== domainId),
      );
    } catch (removeError) {
      setActionErrors((current) => ({
        ...current,
        [domainId]: errorMessage(removeError, "Could not remove the domain."),
      }));
    } finally {
      setBusyId(null);
    }
  };

  const hintIsApex = hintHost.includes(".") && isApexHostname(hintHost);
  const hintApex = hintHost.startsWith("www.") ? hintHost.slice(4) : null;
  const pairAdded = (domains ?? []).some(
    (domain) => domain.hostname === hintApex,
  );

  return (
    <div className="space-y-3">
      <Hint>
        Use your own domain for this site.
        {siteSubdomain
          ? " Once a domain is primary, your free Hazumu address redirects to it."
          : ""}
      </Hint>

      {domains === null && !error && <Hint>Loading domains…</Hint>}

      {(domains ?? []).map((domain) => (
        <DomainCard
          actionError={actionErrors[domain.id] ?? ""}
          busy={busyId === domain.id}
          copied={copied}
          domain={domain}
          isPublished={isPublished}
          isSubscribed={isSubscribed}
          key={domain.id}
          onCheck={() => void check(domain.id)}
          onCopy={(value) => void copy(value)}
          onRemove={() => void remove(domain.id)}
          onSetPrimary={() => void makePrimary(domain.id)}
        />
      ))}

      {isSubscribed ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Input
              aria-label="Custom domain"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={adding}
              onBlur={() => setHintHost(normalizeDomainInput(input))}
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void add();
              }}
              placeholder="www.example.com"
              spellCheck={false}
              type="text"
              value={input}
            />
            <Button
              className="shrink-0"
              disabled={adding || !input.trim()}
              onClick={() => void add()}
              size="sm"
              variant="secondary"
            >
              {adding ? "Adding…" : "Add domain"}
            </Button>
          </div>
          <Hint>
            {hintIsApex
              ? `${hintHost} is a bare domain — add www.${hintHost} too. A CNAME works everywhere on www; the bare domain needs forwarding or flattening (we’ll show the options once added).`
              : hintApex && !pairAdded
                ? `Tip: also add ${hintApex} and forward it to ${hintHost}, so visitors who skip the www reach you.`
                : "Use a www address like www.example.com — it works at every DNS provider."}
          </Hint>
        </div>
      ) : (
        <Notice tone="info">
          <Hint>Custom domains are included with Hazumu Pro.</Hint>
          <Button
            className="mt-3"
            disabled={subscribing}
            fullWidth
            onClick={onSubscribe}
            variant="accent"
          >
            <Sparkles size={16} />
            {subscribing ? "Opening checkout…" : "Subscribe to add a domain"}
          </Button>
        </Notice>
      )}

      {error && <Notice>{error}</Notice>}
    </div>
  );
};
